// The Training pages read TrainingJobs, TrainingRuntimes and Environments as the API serves them (resources.md
// §4.8-§4.10, api/nodus/v1beta1/trainingjob_types.go). These are the fields the pages show; the rest stays in YAML.
import type { CommonStatus, Condition, KObject } from "@nodus/api";
import type { Topology } from "@nodus/api/gen/nodus_dev_v1";
import type { CostBySegment, CostStatus } from "@nodus/ui";
import { catalogGpuName, type GpuNamer } from "../console/gpu-names";

export type Scope = { org: string; project: string };

/** Compact metrics retain small nonzero losses without showing floating-point noise. */
export function formatTrainingMetric(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return Math.abs(value) !== 0 && Math.abs(value) < 0.001
    ? value.toExponential(2) : value.toFixed(4).replace(/\.?0+$/, "");
}

/** Catalog Environments and TrainingRuntimes live in this project; every project reads them. */
export const CATALOG_PROJECT = "nodus";

export type TrainingData = { volume: string; revision?: number; path?: string; format?: string; columns?: Record<string, string> };

export type TrainingJobSpec = {
  runtime: string;
  mode?: "Train" | "Evaluate";
  model?: { uri?: string; volume?: { name: string; revision?: number }; secret?: string };
  teacher?: { uri?: string };
  exportTo?: { volume: string };
  task?: string;
  environment?: { name: string; trainTasks?: number; heldOutTasks?: number; seed?: number };
  data?: TrainingData;
  parameters?: Record<string, unknown>;
  resources?: { nodes?: number; gpu?: { type?: string[]; count?: number }; cpu?: string; memory?: string };
  distributed?: { nodes?: number; totalGPUs?: number; gpusPerNode?: number; network?: string };
  placement?: { pool?: string };
  maxCostUSD?: string;
  state?: string;
};

export type PhaseSummary = { tasks: number; passed: number; passRate?: string; meanReward?: string; errors: number; inProgress: number };

export type TrainingJobStatus = {
  phase?: string;
  reason?: string;
  message?: string;
  fix?: string;
  conditions?: Condition[];
  job?: string;
  progress?: { stage?: string };
  provenance?: { runtimeDigest?: string; environmentDigest?: string; modelRevision?: string; managed: boolean };
  summary?: {
    baseline?: PhaseSummary;
    evaluation?: PhaseSummary;
    comparison?: { comparable: boolean; measurable: boolean; changePP?: string; resolutionPP?: string; reason: string; note?: string };
  };
  diagnostics?: { rewardSignal?: string; weightsChanged?: boolean };
  metrics?: { step?: number; totalSteps?: number; loss?: string; tokensPerSecond?: string };
  outputs?: { name: string; sizeBytes?: number }[];
  cost?: CostStatus & { bySegment?: CostBySegment };
};

export type TrainingJob = KObject<TrainingJobSpec, TrainingJobStatus>;

export type RuntimePreset = { model: string; method: string; quantization?: string; resources: { gpu?: { type?: string[]; count?: number } } };

export type TrainingRuntime = KObject<
  {
    image: string;
    launcher?: string;
    task?: string;
    modes?: string[];
    parameters?: Record<string, unknown>;
    defaults?: { parameters?: Record<string, unknown> };
    presets?: RuntimePreset[];
    requires?: { environment?: boolean; data?: boolean; model?: boolean; teacher?: boolean };
  },
  CommonStatus & { managed?: boolean; imageDigest?: string }
>;

export type EnvironmentExample = {
  name: string;
  mode: string;
  runtime: string;
  model?: string;
  parameters?: Record<string, unknown>;
  tasks?: number;
  baseline?: { passRate: number };
  trained?: { passRate: number };
  changePP?: number;
  runtimeSeconds?: number;
};

export type Environment = KObject<
  {
    version: string;
    package?: { image?: string; pip?: { name: string; version: string; index?: string }; loader?: string };
    publisher?: string;
    category?: string;
    summary?: string;
    sourceURL?: string;
    readiness?: string;
    modes?: string[];
    rewardType?: string;
    sampleTask?: unknown;
    examples?: EnvironmentExample[];
    splits?: { train: { size: number }; test: { size: number } };
  },
  CommonStatus & { digest?: string; splits?: { train: number; test: number } }
>;

/** One row of the `taskevents` subresource (resources.md §4.8). */
export type TaskEvent = {
  id: string;
  attempt?: string;
  eventType: string;
  phase?: string;
  taskId?: string;
  taskAttempt?: number;
  outcome?: "Passed" | "Failed" | "Error";
  reward?: number;
  durationMs?: number;
  message?: string;
  /** The prompt and the model's completion, clipped by the server to fit the event. */
  prompt?: string;
  completion?: string;
  grading?: { verdict?: string; exitCode?: number };
};

/** The response of POST …/trainingruntimes/{name}/datasetpreviews. */
export type DatasetPreview = {
  rows: number;
  format?: string;
  layout?: string[];
  columns?: string[];
  errors: string[];
  duplicates: number;
  distinctPrompts: number;
  samples: unknown[];
};

/** status.progress.stage in order; CachingModel is the controller's, the rest the trainer's or the export's. */
export const STAGES = ["CachingModel", "Baseline", "Training", "Evaluating", "Exporting"] as const;

export const STAGE_WORDS: Record<string, string> = { CachingModel: "Caching model" };

/** A runtime's words for the guided form and the list's second line. */
export const TASK_WORDS: Record<string, string> = {
  SFT: "Supervised fine-tuning",
  Pretrain: "Pretraining",
  DPO: "Preference tuning (DPO)",
  ORPO: "Preference tuning (ORPO)",
  KTO: "Preference tuning (KTO)",
  Reward: "Reward model",
  GRPO: "Reinforcement learning (GRPO)",
  Distill: "Distillation",
  Evaluate: "Evaluation",
};

export function taskWord(task: string | undefined): string {
  return (task && TASK_WORDS[task]) || task || "Training";
}

/** `nodus/<name>@<version>` for a catalog environment, `<name>@<version>` for a project one. */
export function environmentRef(env: Environment): string {
  const base = env.metadata.namespace === CATALOG_PROJECT ? `${CATALOG_PROJECT}/${env.metadata.name}` : env.metadata.name;
  return `${base}@${env.spec?.version ?? ""}`;
}

/** The runtime reference a TrainingJob's spec.runtime takes: `nodus/<name>` for the catalog. */
export function runtimeRef(rt: TrainingRuntime): string {
  return rt.metadata.namespace === CATALOG_PROJECT ? `${CATALOG_PROJECT}/${rt.metadata.name}` : rt.metadata.name;
}

/** The compiled Job's immutable topology wins; explicit request fields remain useful before admission. */
export function trainingTopology(tj: TrainingJob, resolved?: Topology) {
  const resources = tj.spec?.resources;
  const distributed = tj.spec?.distributed;
  const nodes = resolved?.nodes ?? distributed?.nodes ?? resources?.nodes ?? (distributed?.totalGPUs ? undefined : 1);
  const gpusPerNode = resolved?.gpusPerNode ?? distributed?.gpusPerNode ?? resources?.gpu?.count ?? (resources?.gpu ? 1 : undefined);
  return { nodes, gpusPerNode, totalGPUs: resolved?.totalGPUs ?? distributed?.totalGPUs ?? (nodes && gpusPerNode ? nodes * gpusPerNode : undefined), network: resolved?.network ?? distributed?.network };
}

/** The resolved shape and offering describe execution; requests provide a fallback before compilation. */
export function shapeOf(tj: TrainingJob, name: GpuNamer = catalogGpuName, ranOn?: string, resolved?: Topology): string {
  const gpu = tj.spec?.resources?.gpu;
  const shape = trainingTopology(tj, resolved);
  const accelerator = resolved?.accelerator ? name(resolved.accelerator)
    : ranOn && name(ranOn) !== ranOn ? name(ranOn) : gpu?.type?.map(name).join(" or ");
  if (!shape.nodes && shape.totalGPUs) return `${taskWord(tj.spec?.task)} · ${shape.totalGPUs} GPUs · topology pending`;
  const per = accelerator ? `${shape.gpusPerNode ?? 1} × ${accelerator}`
    : tj.spec?.resources?.cpu
      ? `${tj.spec.resources.cpu} vCPU${tj.spec.resources.memory ? ` · ${tj.spec.resources.memory} RAM` : ""}`
      : "preset compute";
  return shape.nodes && shape.nodes > 1 ? `Distributed ${taskWord(tj.spec?.task).toLowerCase()} on ${shape.nodes} nodes of ${per}` : `${taskWord(tj.spec?.task)} · ${per}`;
}

/** A pass rate in percent with one decimal ("62.5 %"), or undefined when nothing was scored. */
export function percent(rate: string | number | undefined): string | undefined {
  if (rate === undefined || rate === "") return undefined;
  const n = typeof rate === "number" ? rate : Number(rate);
  return Number.isFinite(n) ? `${(n * 100).toFixed(1)} %` : undefined;
}

export function pp(value: string | number | undefined): string | undefined {
  if (value === undefined || value === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? `${n > 0 ? "+" : ""}${n.toFixed(1)} pp` : undefined;
}
